import React from 'react';
import Highcharts from 'highcharts';
import { FinancialData } from '../../types';
import MChart from './MChart';

interface FinancialMetricsChartProps {
    // data: FinancialData;
    periods : any;
    metrics : any;
    selectedMetrics: string[];
    onChangePeriod?: (period: string) => void;
    type?: string; // 'annual' or 'quarterly'
    height?: number;
}

const FinancialMetricChart: React.FC<FinancialMetricsChartProps> = ({ periods, metrics, selectedMetrics, onChangePeriod, type, height }) => {
    // let { periods, metrics } = data;
    // console.log("FinancialMetricChart Data:", "Selected Metrics:", selectedMetrics, "Type:", type);

    const filteredMetrics = metrics?.filter((metric:any) => selectedMetrics.includes(metric.metricName));
    
    // Generate expanded periods for quarterly view
    const expandedPeriods = React.useMemo(() => {
        if (type === 'annual') return periods;
        
        // For quarterly, expand each year to show all quarters
        const expandedPeriods: string[] = [];
        const quarterMap = new Map<string, any[]>();
        
        // Group quarters by year
        filteredMetrics?.[0]?.periods.forEach((period: any) => {
            const year = period.year.originalYear.toString();
            if (!quarterMap.has(year)) {
                quarterMap.set(year, []);
            }
            quarterMap.get(year)?.push(period);
        });
        
        // Sort years and create quarter periods
        const sortedYears = Array.from(quarterMap.keys()).sort();
        sortedYears.forEach(year => {
            const quarters = quarterMap.get(year) || [];
            quarters.sort((a, b) => (a.year.quarter || 0) - (b.year.quarter || 0));
            
            quarters.forEach(quarter => {
                if (quarter.year.quarter) {
                    expandedPeriods.push(`${year} Q${quarter.year.quarter}`);
                } else {
                    expandedPeriods.push(year);
                }
            });
        });
        
        return expandedPeriods;
    }, [periods, filteredMetrics, type]);

    const getYIndex = (percent: boolean, point: boolean) => {
        if (filteredMetrics.length == 1) return 0;
        let result: number | undefined = undefined;
        if (filteredMetrics.length >= 2) {
            let percentIndex: number | undefined = undefined;
            let pointIndex: number | undefined = undefined;
            let dollarIndex: number | undefined = undefined;
            filteredMetrics.forEach((m, i) => {
                if (m.periods[0].value.percent && percentIndex === undefined) {
                    percentIndex = i;
                }
                if (!m.periods[0].value.formattedValue?.includes('B') && !m.periods[0].value.percent && pointIndex === undefined) {
                    pointIndex = i;
                }
                if (m.periods[0].value.formattedValue?.includes('B') && !m.periods[0].value.percent && dollarIndex === undefined) {
                    dollarIndex = i;
                }
            });
            if (percent) result = percentIndex;
            if (point) result = pointIndex;
            if (!percent && !point) result = dollarIndex;
            return result;
        }
        return result;
    }

    const seriesData = filteredMetrics?.map((metric:any) => {
        const finalPeriods = type === 'annual' ? periods : expandedPeriods;
        
        const values = finalPeriods.map((period:any) => {
            let periodData: any;
            const periodStr = period.toString(); // Convert to string to ensure string methods work
            
            if (type !== 'annual' && periodStr.includes(' Q')) {
                // Handle quarterly periods like "2023 Q1"
                const [year, quarterStr] = periodStr.split(' Q');
                const quarter = parseInt(quarterStr);
                
                periodData = metric.periods.find((p: any) => 
                    p.year.originalYear.toString() === year && p.year.quarter === quarter
                );
            } else if (type !== 'annual') {
                // Handle year-only periods in quarterly mode
                periodData = metric.periods.filter((p: any) => p.year.originalYear.toString() === periodStr);
                
                if (Array.isArray(periodData) && periodData.length > 0) {
                    // Take the first quarter or the single period
                    periodData = periodData[0];
                }
            } else {
                // Annual mode
                const p = metric.periods.sort();
                periodData = p.find((p: any) => p.year.year === periodStr);
            }
            
            if (!periodData) {
                return { value: 0, formattedValue: '', percent: false };
            }
            
            return { 
                value: periodData.value.value, 
                formattedValue: periodData.value.formattedValue, 
                percent: periodData.value.percent 
            };
        });

        const isPercentage = metric.periods.length && metric.periods[0]?.value?.percent;
        const isPoint = metric.periods.length && !metric.periods[0]?.value?.formattedValue?.includes('B') && !isPercentage;
        
        const seriesdata: Highcharts.SeriesOptionsType = {
            name: metric.label,
            type: (isPercentage ? 'line' : isPoint ? 'line' : 'column') as 'line' | 'column',
            yAxis: getYIndex(isPercentage, isPoint),
            data: values.map((v:any, index: number) => ({
                y: isPercentage ? v.value * 100 : v.value,
                formattedValue: v.formattedValue
            })),
            showInLegend: true,
            dashStyle: isPercentage ? 'Solid' : isPoint ? 'Solid' : 'Solid',
            marker: isPercentage ? { enabled: true } : isPoint ? { enabled: true } : undefined,
            tooltip: {
                pointFormatter: function() {
                    const point = this as any;
                    return `<span style="color:${point.color}">${point.series.name}</span>: <b>${point.formattedValue || Highcharts.numberFormat(point.y, 2)}</b><br/>`;
                }
            } as any,
        };
        
        const yaxisData = {
            title: {
                enabled: false,
                text: metric.label,
                style: {
                    color: 'var(--text-secondary)',
                },
            },
            labels: {
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): any {
                    if (isPercentage) {
                        return this.value + '%';
                    } else if (isPoint) {
                        return Highcharts.numberFormat(this.value, 0);
                    } else {
                        return Highcharts.numberFormat(this.value / 1e9, 1) + 'B';
                    }
                },
                style: {
                    color: 'var(--text-secondary)',
                },
            },
            opposite: true,
            gridLineWidth: isPercentage ? 0 : 1,
            min: isPercentage ? 0 : undefined,
        };
        
        return { series: seriesdata, yAxis: yaxisData };
    });
    return (
        <div className="chart-container w-full p-0 rounded-xl bg-theme-secondary pt-1" style={{ marginBottom: '0px', paddingBottom: '8px', position: 'relative', zIndex: 0 }}>
            <MChart 
                periods={type === 'annual' ? periods : expandedPeriods} 
                seriesData={seriesData} 
                selectedPeriod={type}
                onChange={onChangePeriod} 
                height={height}
            />
        </div>
    );
};

export default React.memo(FinancialMetricChart);